import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoSparkles, IoArrowForward } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import { useAuth } from "@/context/AuthContext";
import { useAsyncData } from "@/lib/useAsyncData";
import { SkeletonCards, RetryNotice } from "@/components/DataStates";
import { listFormations, getProgress } from "@/lib/api";

const DISCORD_INVITE = "https://discord.gg/sFCKWpgRg";

export default function Ecole() {
  const nav = useNavigate();
  const { loading, isAuthenticated, isAdmin, user } = useAuth();
  const { data: formations, loading: fLoading, error: fError, reload: fReload } = useAsyncData("formations", listFormations);
  const [badges, setBadges] = useState(0);

  useEffect(() => {
    if (!loading && !isAuthenticated) nav("/connexion", { replace: true, state: { from: { pathname: "/ecole" } } });
  }, [loading, isAuthenticated, nav]);

  useEffect(() => {
    if (isAuthenticated) getProgress().then((p) => setBadges(p.badge_count || 0)).catch(() => {});
  }, [isAuthenticated]);

  const firstName = user?.name ? user.name.split(" ")[0] : "créateur";

  return (
    <DojoShell crumbs={[{ label: "École" }]}>
      {/* Accueil personnalisé — accompagnement */}
      <div style={st.hero}>
        <div style={st.heroGlow} />
        <span style={st.heroKanji}>學</span>
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={st.eyebrow}><IoSparkles size={13} /> BON RETOUR AU DŌJŌ</div>
          <h1 style={st.h1}>Ravi de te revoir, {firstName}.</h1>
          <p style={st.sub}>Chaque parcours est une voie à franchir, porte après porte. On avance à ton rythme — tu es ici chez toi.</p>
        </div>
        {isAdmin && (
          <button style={st.adminBtn} data-testid="ecole-mode-creation" onClick={() => nav("/ecole/creation")}>✎ Mode Création</button>
        )}
      </div>

      {/* Barre de sceaux — gamification */}
      {isAuthenticated && (
        <div style={st.seals} data-testid="ecole-seals">
          <div style={st.sealsHead}>
            <span style={st.sealsTitle}>Sceaux débloqués</span>
            <span style={st.sealsCount}>{badges} / 7</span>
          </div>
          <div style={st.sealRow}>
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} style={{ ...st.seal, ...(i < badges ? st.sealOn : {}) }}>{i < badges ? "✦" : i + 1}</div>
            ))}
          </div>
        </div>
      )}

      <h2 style={st.sectionTitle}>Tes formations</h2>
      {(fLoading && !formations) ? (
        <SkeletonCards count={3} height={340} min={300} />
      ) : fError ? (
        <RetryNotice onRetry={fReload} label="Impossible de charger les formations." />
      ) : (formations || []).length === 0 ? (
        <p style={{ color: "var(--text-secondary)" }}>{"Aucune formation disponible pour l'instant."}</p>
      ) : (
        <div style={st.grid}>
          {(formations || []).map((f) => (
            <button key={f.id} style={st.card} data-testid={`formation-card-${f.id}`} onClick={() => nav(`/ecole/formation/${f.id}`)}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-8px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}>
              <div style={{ ...st.cardImg, backgroundImage: f.cover ? `url(${f.cover})` : "none" }}>
                <span style={st.cardImgScrim} />
                <span style={st.cardKanji}>{f.kanji || "門"}</span>
                {!f.is_published && <span style={st.draft}>Brouillon</span>}
                <div style={st.cardOverlayBody}>
                  <div style={st.cardTitle}>{f.title}</div>
                  <div style={st.cardDesc}>{f.description}</div>
                  <span style={st.cardGo}>Entrer dans le parcours <IoArrowForward size={15} /></span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* CTA Coaching — aussi proéminent que l'espace membre */}
      <button style={st.coaching} data-testid="ecole-coaching-cta" onClick={() => nav("/accompagnement")}>
        <div style={st.coachingGlow} />
        <div style={{ position: "relative", zIndex: 1, flex: 1, textAlign: "left" }}>
          <div style={st.coachingEyebrow}>ACCOMPAGNEMENT PERSONNEL</div>
          <div style={st.coachingTitle}>Réserver un coaching Renzoku</div>
          <div style={st.coachingSub}>Un accompagnement en tête-à-tête pour débloquer ton histoire et accélérer ta progression.</div>
        </div>
        <span style={st.coachingBtn}>Réserver ma séance <IoArrowForward size={18} /></span>
      </button>
    </DojoShell>
  );
}

const st = {
  hero: { position: "relative", overflow: "hidden", display: "flex", gap: 18, alignItems: "center", marginBottom: 22, padding: "28px 26px", borderRadius: 22, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)" },
  heroGlow: { position: "absolute", top: -60, right: -40, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 22%, transparent), transparent 70%)" },
  heroKanji: { fontSize: 72, fontWeight: 800, color: RED, lineHeight: 1, position: "relative", zIndex: 1 },
  eyebrow: { display: "inline-flex", alignItems: "center", gap: 6, color: RED, fontWeight: 800, fontSize: 12, letterSpacing: 1.5 },
  h1: { fontSize: 30, fontWeight: 900, margin: "6px 0 4px", color: "var(--text-primary)" },
  sub: { color: "var(--text-secondary)", fontSize: 15, margin: 0, maxWidth: 620, lineHeight: 1.6 },
  adminBtn: { position: "relative", zIndex: 1, marginLeft: "auto", alignSelf: "flex-start", padding: "10px 16px", borderRadius: 11, border: `1.5px solid ${RED}`, background: "var(--bg-secondary)", color: RED, fontWeight: 800, fontSize: 13.5, cursor: "pointer" },
  seals: { marginBottom: 30, padding: "18px 20px", borderRadius: 18, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)" },
  sealsHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 },
  sealsTitle: { fontSize: 12, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--text-secondary)" },
  sealsCount: { fontSize: 20, fontWeight: 900, color: RED },
  sealRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  seal: { flex: 1, minWidth: 36, height: 44, borderRadius: 10, border: "1.5px dashed var(--border-c)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "var(--text-secondary)", background: "var(--bg-tertiary)" },
  sealOn: { border: "none", background: `linear-gradient(135deg, ${RED}, color-mix(in srgb, var(--brand) 60%, #000))`, color: "#fff", boxShadow: `0 6px 16px color-mix(in srgb, var(--brand) 40%, transparent)` },
  sectionTitle: { fontSize: 14, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--text-secondary)", margin: "0 0 14px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 20, marginBottom: 34 },
  card: { position: "relative", overflow: "hidden", display: "block", textAlign: "left", minHeight: 340, padding: 0, borderRadius: 22, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)", cursor: "pointer", transition: "transform .25s ease" },
  cardImg: { position: "relative", height: "100%", minHeight: 340, backgroundSize: "cover", backgroundPosition: "center", background: "linear-gradient(160deg, #2a0707, #0a0303)" },
  cardImgScrim: { position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.45) 45%, rgba(0,0,0,0.9) 100%)" },
  cardKanji: { position: "absolute", right: 18, top: 14, fontSize: 72, fontWeight: 800, color: "rgba(255,255,255,0.16)" },
  cardOverlayBody: { position: "absolute", left: 0, right: 0, bottom: 0, padding: "24px 22px" },
  cardTitle: { fontSize: 23, fontWeight: 900, color: "#fff", textShadow: "0 2px 16px rgba(0,0,0,0.6)" },
  cardDesc: { fontSize: 13.5, color: "rgba(255,255,255,0.85)", marginTop: 8, lineHeight: 1.5, maxWidth: 420 },
  cardGo: { display: "inline-flex", alignItems: "center", gap: 8, marginTop: 16, color: "#fff", fontWeight: 800, fontSize: 14, background: RED, padding: "8px 16px", borderRadius: 999 },
  draft: { position: "absolute", left: 16, top: 16, fontSize: 11, fontWeight: 800, color: "#fff", background: "rgba(0,0,0,0.5)", padding: "3px 10px", borderRadius: 999 },
  coaching: { position: "relative", overflow: "hidden", width: "100%", display: "flex", alignItems: "center", gap: 20, padding: "30px 28px", borderRadius: 24, border: "none", cursor: "pointer", background: `linear-gradient(120deg, ${RED}, color-mix(in srgb, var(--brand) 55%, #000))`, boxShadow: `0 20px 50px color-mix(in srgb, var(--brand) 35%, transparent)`, animation: "coachBreath 3.2s ease-in-out infinite", flexWrap: "wrap" },
  coachingGlow: { position: "absolute", top: -80, left: -40, width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.25), transparent 70%)" },
  coachingEyebrow: { color: "rgba(255,255,255,0.85)", fontWeight: 800, fontSize: 12, letterSpacing: 2 },
  coachingTitle: { color: "#fff", fontSize: 26, fontWeight: 900, margin: "6px 0 6px" },
  coachingSub: { color: "rgba(255,255,255,0.9)", fontSize: 14.5, maxWidth: 560, lineHeight: 1.55 },
  coachingBtn: { position: "relative", zIndex: 1, display: "inline-flex", alignItems: "center", gap: 8, background: "#fff", color: RED, fontWeight: 900, fontSize: 15, padding: "14px 22px", borderRadius: 999, whiteSpace: "nowrap" },
};

if (typeof document !== "undefined" && !document.getElementById("ecole-kf")) {
  const el = document.createElement("style");
  el.id = "ecole-kf";
  el.textContent = "@keyframes coachBreath{0%,100%{transform:scale(1)}50%{transform:scale(1.012)}}";
  document.head.appendChild(el);
}
